import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { BaseStateInterface } from './basestate.state';
import { HttpClient, HttpParams } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { UrlService } from '../services/datasources/url.service';
import { FileInfoDto } from 'app/@types/dto/file-info-dto.model';
import { AdvancedMeasureAction } from './advanced-measure.state';

export interface FileUploadStateModel extends BaseStateInterface {
    files: FileInfoDto[];
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace FileUploadAction {
    export class GetFiles {
        static readonly type = '[FileUpload] Get Files';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public measureRowId?: string,
        ) {}
    }

    export class UploadFile {
        static readonly type = '[FileUpload] Upload File';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public file: File,
            public measureRowId?: string,
        ) {}
    }

    export class DeleteFile {
        static readonly type = '[FileUpload] Delete File';
        constructor(
            public organizationId: string,
            public organizationEntityId: string,
            public fileId: string,
            public measureRowId?: string,
        ) {}
    }

    export class ClearFiles {
        static readonly type = '[FileUpload] Clear Files';
    }
}

const defaults: FileUploadStateModel = {
    files: [],
    isInit: false,
    isLoading: false,
    errors: [],
};

@State<FileUploadStateModel>({
    name: 'fileUpload',
    defaults,
})
@Injectable()
export class FileUploadState {
    constructor(
        private urlService: UrlService,
        private http: HttpClient,
        private store: Store,
    ) {}

    @Action(FileUploadAction.GetFiles)
    getFiles(ctx: StateContext<FileUploadStateModel>, action: FileUploadAction.GetFiles) {
        ctx.patchState({ isLoading: true });

        let params = new HttpParams().set('organizationEntityId', action.organizationEntityId);
        if (action.measureRowId) {
            params = params.set('questionnaireRowId', action.measureRowId);
        }

        const url = this.urlService.getFileInfoUrl(action.organizationId);
        return this.http.get<FileInfoDto[]>(url, { params }).pipe(
            tap({
                next: (response) => {
                    ctx.patchState({
                        files: response,
                        isLoading: false,
                        isInit: true,
                        errors: [],
                    });
                },
                error: (error: Error) => {
                    ctx.patchState({
                        isLoading: false,
                        errors: [error.message],
                    });
                },
            }),
        );
    }

    @Action(FileUploadAction.UploadFile)
    uploadFile(ctx: StateContext<FileUploadStateModel>, action: FileUploadAction.UploadFile) {
        ctx.patchState({ isLoading: true });
        const formData = new FormData();
        formData.append('file', action.file);

        let params = new HttpParams().set('organizationEntityId', action.organizationEntityId);
        if (action.measureRowId) {
            params = params.set('questionnaireRowId', action.measureRowId);
        }

        const url = this.urlService.getFileUploadUrl(action.organizationId);
        return this.http.post<FileInfoDto>(url, formData, { params }).subscribe({
            next: (response) => {
                ctx.patchState({
                    files: [],
                    isLoading: false,
                    errors: [],
                    isInit: false,
                });
                // Call the state advanced-measure.state to launch the AddFileToState action.
                this.store.dispatch(new AdvancedMeasureAction.AddFileToState(response));
            },
            error: (error: Error) => {
                ctx.patchState({
                    isLoading: false,
                    errors: [error.message],
                });
            },
        });
    }

    @Action(FileUploadAction.DeleteFile)
    deleteFile(ctx: StateContext<FileUploadStateModel>, action: FileUploadAction.DeleteFile) {
        ctx.patchState({ isLoading: true });

        let params = new HttpParams()
            .set('fileId', action.fileId)
            .set('organizationEntityId', action.organizationEntityId);

        if (action.measureRowId) {
            params = params.set('questionnaireRowId', action.measureRowId);
        }

        return this.http.delete(this.urlService.getFileDeleteUrl(action.organizationId), { params }).subscribe({
            next: () => {
                const currentFiles = ctx.getState().files;
                const updatedFiles = currentFiles.filter((file) => file.id !== action.fileId);

                ctx.patchState({
                    files: updatedFiles,
                    isLoading: false,
                    errors: [],
                });
                // Call the state advanced-measure.state to launch the AddFileToState action.
                this.store.dispatch(
                    new AdvancedMeasureAction.RemoveFileToState(action.measureRowId ?? '', action.fileId),
                );
            },
            error: (error: Error) => {
                ctx.patchState({
                    isLoading: false,
                    errors: [error.message],
                });
            },
        });
    }

    @Action(FileUploadAction.ClearFiles)
    clearFiles(ctx: StateContext<FileUploadStateModel>) {
        ctx.patchState({
            files: [],
            isLoading: false,
            errors: [],
        });
    }

    @Selector()
    static getFiles(state: FileUploadStateModel) {
        return state.files;
    }

    @Selector()
    static isLoading(state: FileUploadStateModel) {
        return state.isLoading;
    }

    @Selector()
    static hasErrors(state: FileUploadStateModel) {
        return state.errors.length > 0;
    }
}
